import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Input from "@material-ui/core/Input";
import CircularProgress from "@material-ui/core/CircularProgress";
import Loading from "../shared/Loading";
import { apiPost } from "../../services/api";
import { Button, FormHelperText, TextField } from "@material-ui/core";
import ICategory from "../../models/ICategory";
import IDocumentError from "../../models/IDocumentError";
import LinearProgress from "@material-ui/core/LinearProgress";

interface IUploadDialog {
    open: boolean;
    projectId: string;
    onCancel(event: React.MouseEvent): void;
    onComplete: (error: IDocumentError | null) => void;
    categories: ICategory[] | null;
}

const UploadDialog: React.FC<IUploadDialog> = ({
    open,
    onCancel,
    onComplete,
    projectId,
    categories
}) => {
    const fileMaxSizeInBytes = 104857600;
    const fileMaxSizeInMegabytes =
        Math.round((fileMaxSizeInBytes / 1024 / 1024) * 100) / 100;

    const fileExtensionsNotAllowed = [
        ".exe",
        ".dll",
        ".js",
        ".zip",
        ".bat",
        ".ps",
        ".rar",
        ".7z"
    ];

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            buttons: {
                padding: theme.spacing(4, 8, 4)
            },
            formControl: {
                marginBottom: theme.spacing(4),
                minWidth: 120,
                verticalAlign: "middle"
            },
            selectEmpty: {
                marginTop: theme.spacing(2)
            },
            loading: {
                margin: theme.spacing(8),
                textAlign: "center"
            }
        })
    );

    const validationErrorsState: { file: string | null } = {
        file: null
    };

    const classes = useStyles();
    const [uploading, setUploading] = React.useState(false);
    const [filesToUpload, setFilesToUpload] = React.useState<
        Array<IDocumentFile>
    >([]);
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [validationErrors, setValidationErrors] = React.useState(
        validationErrorsState
    );

    const [category, setCategory] = React.useState(0);
    const [title, setTitle] = React.useState<string | null>(null);
    const [description, setDescription] = React.useState<string | null>(null);
    interface IDocumentFile {
        file: File;
        type: Number;
    }

    const resetFilesToUpload = () => {
        setFilesToUpload([]);
    };

    const resetValidationErrors = () => {
        setValidationErrors({
            file: null
        });
    };

    const handleCancel = event => {
        resetValidationErrors();
        onCancel(event);
    };

    const handleUpload = async () => {
        let uploadedFiles = 0;
        let error: IDocumentError | null = null;

        setUploadProgress(0);
        setUploading(true);

        try {
            for (let document in filesToUpload) {
                const formData = new FormData();
                formData.append("file", filesToUpload[document].file);
                formData.append("type", `${category}`);

                //// we only can have a title and description when uploading a single file
                if (filesToUpload.length === 1) {
                    if (title !== null) {
                        formData.append("title", title);
                    }

                    if (description !== null) {
                        formData.append("description", description);
                    }
                }

                try {
                    await apiPost(
                        `projects/all/${projectId}/documents`,
                        formData
                    );

                    setUploadProgress(
                        Math.floor(
                            (++uploadedFiles / filesToUpload.length) * 100
                        )
                    );
                } catch (e) {
                    error = {
                        ...e,
                        fileName: filesToUpload[document].file.name
                    };
                    break;
                }
            }
        } finally {
            setUploading(false);
            resetFilesToUpload();
            onComplete(error);
        }
    };

    const isValid = errors => {
        return !errors.file;
    };

    const validate = file => {
        let errors: { file: string | null } = { file: null };

        const fileExtensions = fileExtensionsNotAllowed.map(x =>
            x.startsWith(".") ? x : `.${x}`
        );

        if (file.size > fileMaxSizeInBytes) {
            errors = {
                ...errors,
                file: "One or more files exceeds the maximum file size limit"
            };
        }

        if (file.name) {
            const extension = fileExtensions.find(x => file.name.endsWith(x));
            if (extension) {
                errors = {
                    ...errors,
                    file: `File type '${extension}' not allowed`
                };
            }
        }

        return errors;
    };

    const onTitleChange = event => {
        setTitle(event.target.value);
    };

    const onDescriptionChange = event => {
        setDescription(event.target.value);
    };

    const onUploadFileChange = event => {
        const files: Array<any> = [];

        setFilesToUpload([]);
        resetValidationErrors();

        for (let i = 0; i < event.target.files.length; i++) {
            const file = event.target.files[i];
            const errors = validate(file);
            if (!isValid(errors)) {
                setValidationErrors(errors);
                return;
            }

            files.push({ file: file, type: category });
        }

        setFilesToUpload(files);
    };

    const onCategoryChange = event => {
        setCategory(event.target.value);
    };

    return (
        <div>
            <Dialog
                open={open}
                aria-labelledby="form-dialog-title"
                fullWidth={true}
                maxWidth="xs"
            >
                <DialogTitle id="form-dialog-title">
                    Upload Documents
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {uploading
                            ? "Uploading..."
                            : "Create new documents by uploading files."}
                    </DialogContentText>
                    {uploading ? (
                        <div>
                            <div className={classes.loading}>
                                <Loading />
                            </div>
                            <div>
                                {filesToUpload.length > 1 ? (
                                    <LinearProgress
                                        variant="determinate"
                                        value={uploadProgress}
                                    />
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    ) : (
                        <FormGroup>
                            {categories == null ? (
                                <FormControl className={classes.formControl}>
                                    <CircularProgress size={14} />
                                </FormControl>
                            ) : (
                                <FormControl className={classes.formControl}>
                                    <InputLabel htmlFor="type-native-simple">
                                        Category
                                    </InputLabel>
                                    <Select
                                        native
                                        inputProps={{
                                            name: "type",
                                            id: "type-native-simple"
                                        }}
                                        onChange={onCategoryChange}
                                    >
                                        {categories.map(item => {
                                            return (
                                                <option
                                                    key={item.id}
                                                    value={item.id}
                                                >
                                                    {item.name}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}

                            <TextField
                                label="Title"
                                defaultValue={title}
                                placeholder="Document title"
                                onChange={onTitleChange}
                                disabled={filesToUpload.length > 1}
                                helperText={
                                    filesToUpload.length > 1
                                        ? "Document title can only be set when uploading single files."
                                        : "File name will be used if not given."
                                }
                            />

                            <TextField
                                multiline
                                rowsMax={4}
                                disabled={filesToUpload.length > 1}
                                rows={4}
                                label="Description"
                                placeholder="Document descrption"
                                defaultValue={description}
                                onChange={onDescriptionChange}
                                helperText={
                                    filesToUpload.length > 1
                                        ? "Document description can only be set when uploading single files."
                                        : "This value is visible to all users."
                                }
                            />

                            <FormControl className={classes.formControl}>
                                <InputLabel></InputLabel>
                                <Input
                                    inputProps={{
                                        multiple: true
                                    }}
                                    type="file"
                                    onChange={onUploadFileChange}
                                    error={!!validationErrors.file}
                                />
                                <FormHelperText>
                                    The maximum file upload size is{" "}
                                    {fileMaxSizeInMegabytes}MB
                                </FormHelperText>
                                <FormHelperText error={true}>
                                    {validationErrors.file}
                                </FormHelperText>
                            </FormControl>
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="primary">
                        Close
                    </Button>
                    {!uploading ? (
                        <Button
                            color="primary"
                            onClick={handleUpload}
                            disabled={
                                !categories ||
                                !filesToUpload.length ||
                                !isValid(validationErrors)
                            }
                        >
                            Upload
                        </Button>
                    ) : (
                        <></>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UploadDialog;
